import PropTypes from 'prop-types';
import React from 'react';

import getEnv from '@wetransfer/transfer/config/env';
import Analytics from '@wetransfer/transfer/modules/analytics';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import request from '@wetransfer/transfer/modules/http/axios/request';
import Logger from '@wetransfer/transfer/modules/logger';

import renderHomeUpsellBanner from './Hydrate/UpsellBanner';
import PanelLoading from './loading';

// A dictionary of `<path>:<callback>`
const SCRIPTS = {};
let SIDELOADED = false;

// Export our Content component, used for static content.
export default class Content extends React.Component {
  static propTypes = {
    route: PropTypes.object,
    page: PropTypes.object,
    locale: PropTypes.string,
    setReturnPath: PropTypes.func,
  };

  state = {
    path: false,
    content: false,
    failed: false,
    loading: true,
  };

  // This is keeps track of the current script context (if applicable)
  scriptContext = null;

  // Configure content for a specific path
  static configure(path, method) {
    SCRIPTS[path] = method;
  }

  // Check if the content we need has already been sideloaded
  hasInlineContent = () => {
    // Let's see if there is a panel element in the body.
    const inlineContent = document.querySelector('body > .panel[data-path]');

    if (!inlineContent) {
      return false;
    }

    // Alright, remove from body - we're done with this.
    document.body.removeChild(inlineContent);

    // Check if the sideloaded path actually matches our current path
    if (inlineContent.getAttribute('data-path') !== this.props.route.path) {
      return false;
    }

    // Update the state
    SIDELOADED = true;
    this.updateContents(this.props.route.path, inlineContent.innerHTML);

    return true;
  };

  cleanupContents = () => {
    // If there is an active script context, deal with it.
    if (this.scriptContext) {
      if (typeof this.scriptContext['onCleanup'] === 'function') {
        this.scriptContext.onCleanup();
      }
      this.scriptContext = null;
    }

    Browser.isMobile && this.toggleEventListener('removeEventListener');
  };

  toggleEventListener = (listener) => {
    const signupButton = document.querySelector('#signup_button');

    if (signupButton) {
      signupButton[listener]('click', this.onTransferUpgradeClick, false);
    }
  };

  onTransferUpgradeClick = () => {
    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_transfer_upgrade_button',
      label: 'mobile_homepage', //optional
    });
  };

  updateContents = (path, content) => {
    // First clean up old content
    this.cleanupContents();

    let node = document.createElement('div');
    node.innerHTML = content;
    let returnLink = node.querySelector('.panel__return-path');

    if (returnLink) {
      this.props.page.setReturnPath(returnLink.href, returnLink.innerText);
      returnLink = null;
    }
    node = null;

    this.setState({
      path,
      content,
      loading: false,
    });
  };

  // Load content (without layout) via XHR.
  loadRemoteContent = async (path) => {
    // Set the current state to loading.
    this.setState({
      path,
      failed: false,
      loading: true,
    });

    // Verify that this was successful
    try {
      const response = await request({
        path: `${getEnv().NEXT_PUBLIC_API_URL}/static${path}`,
        responseType: 'text',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      this.updateContents(path, response.body);
    } catch (error) {
      this.setState({
        loading: false,
        failed: true,
      });
    }
  };

  // NOTE: Content, not Component. But similar idea.
  contentDidMount = () => {
    if (this.state.loading) {
      return;
    }

    this.executeScript();

    // TODO: Going to be deleted after test is implemented
    renderHomeUpsellBanner();
  };

  scrollTo = (...args) => {
    const page = this.props.page;

    if (!page) {
      return;
    }
    const scrollable = page.scrollableNode;

    if (!scrollable) {
      return;
    }
    scrollable.scrollTo(...args);
  };

  updateScrollbar = () => {
    // Trigger scrollbar check
    const page = this.props.page;

    if (page && page.scrollableNode) {
      page.scrollableNode.shouldShowOverflow();
    }
  };

  preloadImages = async () => {
    const images = this.node.querySelectorAll('img');

    if (!images.length) {
      return;
    }

    const loaders = [];

    for (let i = images.length; i--; ) {
      loaders.push(
        new Promise((resolve) => {
          images[i].addEventListener('load', resolve, false);
          images[i].addEventListener('error', resolve, false);
        })
      );
    }

    await Promise.all(loaders);
    this.updateScrollbar();
  };
  // Execute script for content, if applicable
  executeScript = () => {
    // If there is already script context, and it's this very same page
    // we don't need to re-execute the script, unless it's subscribed to
    // the store for render updates.
    if (
      this.scriptContext &&
      this.scriptContext.path === this.state.path &&
      this.scriptContext.subscribeToStore !== true
    ) {
      return;
    }

    // If there is an existing script context (different page), cleanup first
    // unless it's on the same page (which would only happen for store updates)
    if (this.scriptContext && this.scriptContext.path !== this.state.path) {
      if (typeof this.scriptContext['onCleanup'] === 'function') {
        this.scriptContext.onCleanup();
      }
      this.scriptContext = null;
    }

    // If there is a script for the current path, let's create a context for it
    // and then execute it.
    if (this.state.path in SCRIPTS) {
      this.scriptContext = {
        subscribeToStore: false,
        onCleanup: null,
        path: this.state.path,
        scrollTo: this.scrollTo,
        node: this.node,
        setReturnPath: this.props.setReturnPath,
      };

      SCRIPTS[this.state.path].call(null, this.scriptContext);

      if (typeof this.scriptContext['onCleanup'] !== 'function') {
        Logger.warn(
          `NOTE: the script for ${this.state.path} does not have a cleanup method.`
        );
      }
    }

    Browser.isMobile && this.toggleEventListener('addEventListener');
  };

  // If the component has mounted, we'll need to activate the content.
  componentDidMount() {
    // If there is inline content, we'll use that.
    if (this.hasInlineContent()) {
      return;
    }

    this.loadRemoteContent(this.props.route.path);
    this.contentDidMount();
  }

  // When the component is unmounted, we might need to do a cleanup
  componentWillUnmount() {
    this.cleanupContents();
  }

  // New path (or state) while already mounted, let's see if we need to do
  // a request
  componentDidUpdate(prevProps, prevState) {
    // When the component updates, check for new language
    if (this.props.locale !== prevProps.locale) {
      this.loadRemoteContent(this.props.route.path);
    }

    if (this.state.loading) {
      return;
    }

    // If the new path differs from the current path, let's load a new page
    if (this.props.route.path !== this.state.path) {
      this.loadRemoteContent(this.props.route.path);

      return;
    }

    // Only immediately after load
    if ((prevState.loading && !this.state.loading) || SIDELOADED) {
      SIDELOADED = false;
      this.updateScrollbar();
      this.preloadImages();
    }

    // Activate our content
    this.contentDidMount();
  }

  render() {
    // Show a loading indicator
    if (this.state.loading || this.state.failed) {
      return <PanelLoading failed={this.state.failed} />;
    }

    // Show the content (using the dangerous innerHTML)
    return (
      <div
        ref={(node) => (this.node = node)}
        dangerouslySetInnerHTML={{ __html: this.state.content }}
      />
    );
  }
}
